import { FormEvent, type ReactNode, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { useAuth } from "@/lib/auth-provider";
import {
  normalizeCrmProgramPreviewFacts,
  validateCrmProgramPreviewBoundary,
  validateCrmProgramPreviewEvidenceContract,
  type CrmProgramPreviewFactValue as CrmProgramFactValue,
} from "@/lib/crm-preview-fact-safety";

type CrmProgramRuleCondition =
  | { operator: "default" }
  | { operator: "all"; conditions: CrmProgramRuleCondition[] }
  | { operator: "any"; conditions: CrmProgramRuleCondition[] }
  | { operator: "fact_present"; factKey: string }
  | { operator: "fact_absent"; factKey: string }
  | { operator: "fact_equals"; factKey: string; value: CrmProgramFactValue }
  | { operator: "fact_min"; factKey: string; min: number }
  | { operator: "date_due"; factKey: string }
  | { operator: "date_within_days"; factKey: string; days: number }
  | { operator: "date_past_within_days"; factKey: string; minDays: number; maxDays: number }
  | { operator: "score_min"; min: number }
  | { operator: "score_band_is"; band: string }
  | { operator: "score_band_in"; bands: string[] };

interface CrmProgramScoreBand {
  key: string;
  label: string;
  minScore: number;
}

interface CrmProgramScoreSignal {
  key: string;
  label: string;
  factKey: string;
  points: number;
  reasonCode: string;
  description: string;
  condition?: CrmProgramRuleCondition;
}

interface CrmProgramScoreComponent {
  key: string;
  label: string;
  maxScore: number;
  signals: CrmProgramScoreSignal[];
}

interface CrmProgramIntentRule {
  key: string;
  intentClass: string;
  label: string;
  when: string;
  priority: number;
  condition?: CrmProgramRuleCondition;
}

interface CrmProgramQualificationGate {
  key: string;
  label: string;
  when: string;
  effect: "allow" | "soft_cap" | "hard_cap";
  condition?: CrmProgramRuleCondition;
  maxScore?: number;
  reasonCode?: string;
}

interface CrmProgramTaskRule {
  key: string;
  triggerIntent: string;
  actionRef: string;
  templateKey: string;
  title: string;
  description?: string;
  taskType?: string;
  priority?: string;
  dueInHours: number;
  nextAction?: string;
  scriptTemplate?: string;
}

interface CrmProgramScheduledTaskRule {
  key: string;
  taskType: string;
  title: string;
  description: string;
  priority: string;
  dueDays: number;
  reasonCode: string;
  condition: CrmProgramRuleCondition;
  nextAction?: string;
  scriptTemplate?: string;
}

interface CrmProgramProjectionRule {
  key: string;
  targetField: string;
  source: string;
  piiSafe: boolean;
}

interface CrmProgramConfigResponse {
  source: "default" | "saved";
  programKey: string;
  programVersion: string;
  tenantSlug: string;
  productAppKey: string;
  scoreRange: { min: number; max: number };
  scoreBands: CrmProgramScoreBand[];
  scoreComponents: CrmProgramScoreComponent[];
  intentRules: CrmProgramIntentRule[];
  qualificationGates?: CrmProgramQualificationGate[];
  taskRules: CrmProgramTaskRule[];
  scheduledTaskRules?: CrmProgramScheduledTaskRule[];
  personProjectionRules: CrmProgramProjectionRule[];
  eventContract: {
    sourceEventFamilies: string[];
    projectedEventTypes: string[];
  };
}

type CrmProgramConfigPayload = Omit<CrmProgramConfigResponse, "source">;

interface CrmProgramPolicyDecision {
  score: number;
  scoreBeforeQualification: number;
  scoreBand: string;
  intentClass: string;
  matchedIntentRuleKey: string;
  qualificationGate: "unknown" | "pass" | "soft_cap" | "hard_cap";
  qualificationGateResults?: Array<{
    key: string;
    effect: "allow" | "soft_cap" | "hard_cap";
    matched: boolean;
    maxScore: number | null;
    appliedCapScore: number | null;
    reasonCode: string | null;
  }>;
  reasonCodes: string[];
  componentScores: Record<string, number>;
  signalScores: Array<{
    componentKey: string;
    signalKey: string;
    reasonCode: string;
    points: number;
  }>;
}

interface CrmProgramFactCoverage {
  configuredFactKeys: string[];
  suppliedFactKeys: string[];
  missingConfiguredFactKeys: string[];
  unusedSuppliedFactKeys: string[];
}

interface CrmProgramPreviewEvidence {
  version: "stero.crm.program-policy-preview-evidence.v1";
  configDigestSha256: string;
  coverageStatus: "complete" | "complete_with_unused_supplied_facts" | "missing_configured_facts";
  decisionSummary: {
    score: number;
    scoreBeforeQualification: number;
    scoreBand: string;
    intentClass: string;
    matchedIntentRuleKey: string;
    qualificationGate: "unknown" | "pass" | "soft_cap" | "hard_cap";
    componentScores: Record<string, number>;
  };
  matchedSignalKeys: string[];
  matchedReasonCodes: string[];
  matchedQualificationGateKeys?: string[];
  matchedQualificationGateReasonCodes?: string[];
  taskRecommendationKeys: string[];
  scheduledTaskRecommendationKeys?: string[];
  projectionRuleKeys: string[];
  counts: {
    configuredFactKeys: number;
    suppliedFactKeys: number;
    missingConfiguredFactKeys: number;
    unusedSuppliedFactKeys: number;
    matchedSignals: number;
    matchedReasonCodes: number;
    matchedQualificationGates?: number;
    matchedQualificationGateReasonCodes?: number;
    taskRecommendations: number;
    scheduledTaskRecommendations?: number;
    projectionRules: number;
  };
  rawFactsVisible: false;
  targetWritesExecuted: false;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

interface CrmProgramPolicyPreviewResponse {
  routeId: "stero.crm.program-policy-preview.v1";
  source: "default" | "saved" | "draft";
  programKey: string;
  programVersion: string;
  tenantSlug: string;
  productAppKey: string;
  configDigestSha256: string;
  decision: CrmProgramPolicyDecision;
  factKeys: string[];
  factCoverage: CrmProgramFactCoverage;
  previewEvidence: CrmProgramPreviewEvidence;
  previewEvidenceDigestSha256: string;
  taskRecommendations: Array<{
    key: string;
    actionRef: string;
    templateKey: string;
    title: string;
    description?: string | null;
    taskType?: string | null;
    priority?: string | null;
    dueInHours: number;
    nextAction?: string | null;
    hasScriptTemplate?: boolean;
  }>;
  scheduledTaskRecommendations?: Array<{
    key: string;
    taskType: string;
    title: string;
    description: string;
    priority: string;
    dueDays: number;
    dueAt: string;
    reasonCode: string;
    nextAction?: string | null;
    hasScriptTemplate?: boolean;
    targetWritesExecuted: false;
  }>;
  scheduledPreviewNow?: string;
  projectionPreview: CrmProgramProjectionRule[];
  rawFactsVisible: boolean;
  targetWritesExecuted: boolean;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

interface CrmProgramPolicyPreviewSuiteCase {
  key: string;
  label: string | null;
  expectationStatus: "not_specified" | "met" | "mismatched";
  expectationMismatches: string[];
  previewEvidence: CrmProgramPreviewEvidence;
  previewEvidenceDigestSha256: string;
}

interface CrmProgramPolicyPreviewSuiteResponse {
  routeId: "stero.crm.program-policy-preview-suite.v1";
  source: "default" | "saved" | "draft";
  programKey: string;
  programVersion: string;
  tenantSlug: string;
  productAppKey: string;
  configDigestSha256: string;
  fixtureSource?: "request" | "empty" | "saved";
  fixtureSuiteDigestSha256?: string | null;
  fixturePolicyConfigDigestSha256?: string | null;
  fixturePolicyConfigDigestMatchesPreviewConfig?: boolean | null;
  suite: {
    version: "stero.crm.program-policy-preview-suite.v1";
    status: "ready" | "needs_attention";
    aggregateFactCoverage: CrmProgramFactCoverage;
    cases: CrmProgramPolicyPreviewSuiteCase[];
    counts: {
      cases: number;
      casesWithExpectations: number;
      expectationMismatches: number;
      configuredFactKeys: number;
      suppliedFactKeys: number;
      missingConfiguredFactKeys: number;
      unusedSuppliedFactKeys: number;
      matchedSignals: number;
      matchedQualificationGates?: number;
      matchedQualificationGateReasonCodes?: number;
      taskRecommendations: number;
    };
    rawFactsVisible: false;
    targetWritesExecuted: false;
    servingDecision: "NOT_READY_FOR_SERVING";
    cutoverDecision: "NOT_READY_FOR_CUTOVER";
  };
  suiteDigestSha256: string;
  rawFactsVisible: false;
  targetWritesExecuted: false;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

interface CrmProgramPreviewSuiteExpected {
  intentClass?: string;
  scoreBand?: string;
  qualificationGate?: "unknown" | "pass" | "soft_cap" | "hard_cap";
  matchedQualificationGateReasonCodes?: string[];
  minScore?: number;
  maxScore?: number;
}

type CrmProgramPreviewSuitePayload = {
  suiteKey?: string;
  suiteVersion?: string;
  cases: Array<{
    key: string;
    label?: string;
    facts: Record<string, CrmProgramFactValue>;
    expected?: CrmProgramPreviewSuiteExpected;
  }>;
};

interface CrmProgramPreviewSuiteFixtures {
  version: "stero.crm.program-policy-preview-suite-fixtures.v1";
  suiteKey: string;
  suiteVersion: string;
  tenantSlug: string;
  productAppKey: string;
  policyConfigDigestSha256: string | null;
  cases: CrmProgramPreviewSuitePayload["cases"];
  counts: {
    cases: number;
    casesWithExpectations: number;
    suppliedFactKeys: number;
  };
  digestSha256: string;
  rawFactsVisible: false;
  targetWritesExecuted: false;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

interface CrmProgramPreviewSuiteFixturesResponse {
  routeId: "stero.crm.program-policy-preview-suite-fixtures.v1";
  source: "empty" | "saved";
  tenantSlug: string;
  productAppKey: string;
  currentPolicyConfigDigestSha256: string;
  policyConfigDigestMatchesCurrent: boolean | null;
  fixtures: CrmProgramPreviewSuiteFixtures | null;
  saved: {
    key: "crm.program.preview_suite_fixtures";
    updatedAt: string;
    updatedBy: string;
  } | null;
  rawFactsVisible: false;
  targetWritesExecuted: false;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

interface CrmProgramPolicyReviewResponse {
  routeId: "stero.crm.program-policy-review.v1";
  version: "stero.crm.program-policy-review.v1";
  source: "default" | "saved" | "draft";
  status: "ready" | "needs_attention" | "blocked";
  counts: {
    scoreBands: number;
    scoreComponents: number;
    scoreSignals: number;
    intentRules: number;
    qualificationGates: number;
    taskRules: number;
    scheduledTaskRules: number;
    projectionRules: number;
    sourceEventFamilies: number;
    projectedEventTypes: number;
  };
  findings: Array<{
    severity: "error" | "warning" | "info";
    code: string;
    message: string;
    path?: string;
  }>;
  targetActionRefs: string[];
  unknownActionRefs: string[];
  manualReviewActionRefs: string[];
  targetWritesExecuted: boolean;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

interface CrmProgramPolicyAuditResponse {
  routeId: "stero.crm.program-policy-audit.v1";
  source: "default" | "saved";
  programKey: string;
  programVersion: string;
  tenantSlug: string;
  productAppKey: string;
  configDigestSha256: string;
  saved: {
    key: "crm.program.config";
    updatedAt: string;
    updatedBy: string;
  } | null;
  review: CrmProgramPolicyReviewResponse;
  targetWritesExecuted: boolean;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

interface CrmProgramPolicyReadinessResponse {
  routeId: "stero.crm.program-policy-readiness.v1";
  version: "stero.crm.program-policy-readiness.v1";
  source: "default" | "saved";
  programKey: string;
  programVersion: string;
  tenantSlug: string;
  productAppKey: string;
  configDigestSha256: string;
  status: "ready_for_shadow_dual_run" | "needs_attention" | "blocked";
  policyReviewStatus: "ready" | "needs_attention" | "blocked";
  actionSmoke: {
    routeId: "stero.crm.action-dual-run-smoke.v1";
    smokeVersion: string;
    decision: "READY_FOR_CRM_ACTION_DUAL_RUN_SMOKE_REVIEW" | "BLOCKED_CRM_ACTION_DUAL_RUN_SMOKE_INCOMPLETE";
    summary: {
      registeredActions: number;
      staffGatedMaterializations: number;
      manualOnlyPolicies: number;
      missingPolicyActions: number;
      unsafeMaterializations: number;
    };
    checks: Array<{
      key: string;
      ok: boolean;
      status: "passed" | "blocker";
      severity: "blocker";
      summary: string;
    }>;
    blockers: string[];
    blockedActions: string[];
  };
  counts: {
    policyFindings: number;
    policyErrors: number;
    policyWarnings: number;
    actionSmokeBlockers: number;
    registeredActions: number;
    staffGatedMaterializations: number;
    manualOnlyPolicies: number;
    missingPolicyActions: number;
    unsafeMaterializations: number;
  };
  blockers: string[];
  warnings: string[];
  allowedNextActions: string[];
  targetWritesExecuted: boolean;
  actionsExecuted: boolean;
  mutatesCrm: boolean;
  servingDecision: "NOT_READY_FOR_SERVING";
  cutoverDecision: "NOT_READY_FOR_CUTOVER";
}

const SAMPLE_FACTS = `{
  "icpMatch": true,
  "formSubmissions": 1,
  "bookingRequests": 0,
  "replyCount": 1,
  "websiteVisits": 4,
  "productUsageEvents": 2
}`;

const SAMPLE_SUITE = `{
  "suiteKey": "tenant_policy_fixture_suite",
  "suiteVersion": "draft-v1",
  "cases": [
    {
      "key": "ready_form_lead",
      "label": "Ready lead from form",
      "facts": {
        "icpMatch": true,
        "formSubmissions": 1,
        "replyCount": 1,
        "budgetReady": false
      },
      "expected": {
        "intentClass": "ready",
        "qualificationGate": "soft_cap",
        "matchedQualificationGateReasonCodes": ["budget_not_ready_cap"],
        "minScore": 20
      }
    },
    {
      "key": "nurture_visit_lead",
      "label": "Nurture lead from visits",
      "facts": {
        "websiteVisits": 4
      },
      "expected": {
        "intentClass": "none",
        "scoreBand": "cold"
      }
    }
  ]
}`;

export default function CrmPolicyPage() {
  const { user } = useAuth();
  const canManagePolicyAdminEvidence = user?.role === "admin" || user?.role === "super_admin";
  const [config, setConfig] = useState<CrmProgramConfigResponse | null>(null);
  const [preview, setPreview] = useState<CrmProgramPolicyPreviewResponse | null>(null);
  const [suitePreview, setSuitePreview] = useState<CrmProgramPolicyPreviewSuiteResponse | null>(null);
  const [suiteFixtures, setSuiteFixtures] = useState<CrmProgramPreviewSuiteFixturesResponse | null>(null);
  const [policyReview, setPolicyReview] = useState<CrmProgramPolicyReviewResponse | null>(null);
  const [policyAudit, setPolicyAudit] = useState<CrmProgramPolicyAuditResponse | null>(null);
  const [policyReadiness, setPolicyReadiness] = useState<CrmProgramPolicyReadinessResponse | null>(null);
  const [factsText, setFactsText] = useState(SAMPLE_FACTS);
  const [suiteText, setSuiteText] = useState(SAMPLE_SUITE);
  const [policyText, setPolicyText] = useState("");
  const [policyDirty, setPolicyDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isPreviewingSuite, setIsPreviewingSuite] = useState(false);
  const [isLoadingSuiteFixtures, setIsLoadingSuiteFixtures] = useState(false);
  const [isSavingSuiteFixtures, setIsSavingSuiteFixtures] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);
  const [isLoadingReadiness, setIsLoadingReadiness] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [suiteError, setSuiteError] = useState<string | null>(null);
  const [suiteRegistryMessage, setSuiteRegistryMessage] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [readinessError, setReadinessError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError(null);

    void (async () => {
      try {
        const response = await apiFetch<{ data: CrmProgramConfigResponse }>("/crm/intelligence/program-config", {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setConfig(response.data);
        setPolicyText(formatPolicyPayload(response.data));
        setPolicyDirty(false);
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setLoadError(error instanceof Error ? error.message : "Failed to load CRM policy config.");
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, []);

  async function previewPolicy(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPreviewError(null);
    setPreview(null);

    if (!canManagePolicyAdminEvidence) {
      setPreviewError("Admin role required to run CRM policy previews.");
      return;
    }

    const facts = parseFacts(factsText);
    if (!facts.ok) {
      setPreviewError(facts.error);
      return;
    }

    const policy = policyDirty ? parsePolicyPayload(policyText) : null;
    if (policy && !policy.ok) {
      setPreviewError(policy.error);
      return;
    }

    setIsPreviewing(true);
    try {
      const response = await apiFetch<{ data: CrmProgramPolicyPreviewResponse }>("/crm/intelligence/program-config/preview", {
        method: "POST",
        body: JSON.stringify(policy?.ok ? { facts: facts.value, config: policy.value } : { facts: facts.value }),
      });
      const boundaryError = getPreviewBoundaryError(response.data);
      if (boundaryError) {
        setPreviewError(boundaryError);
        return;
      }
      setPreview(response.data);
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : "Failed to preview CRM policy.");
    } finally {
      setIsPreviewing(false);
    }
  }

  async function previewPolicySuite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSuiteError(null);
    setSuitePreview(null);

    if (!canManagePolicyAdminEvidence) {
      setSuiteError("Admin role required to run CRM policy fixture suites.");
      return;
    }

    const suite = parsePreviewSuitePayload(suiteText);
    if (!suite.ok) {
      setSuiteError(suite.error);
      return;
    }

    const policy = policyDirty ? parsePolicyPayload(policyText) : null;
    if (policy && !policy.ok) {
      setSuiteError(policy.error);
      return;
    }

    setIsPreviewingSuite(true);
    try {
      const response = await apiFetch<{ data: CrmProgramPolicyPreviewSuiteResponse }>("/crm/intelligence/program-config/preview-suite", {
        method: "POST",
        body: JSON.stringify(policy?.ok ? { ...suite.value, config: policy.value } : suite.value),
      });
      const boundaryError = getPreviewSuiteBoundaryError(response.data);
      if (boundaryError) {
        setSuiteError(boundaryError);
        return;
      }
      setSuitePreview(response.data);
    } catch (error) {
      setSuiteError(error instanceof Error ? error.message : "Failed to preview CRM policy suite.");
    } finally {
      setIsPreviewingSuite(false);
    }
  }

  async function previewSavedPolicySuite() {
    setSuiteError(null);
    setSuitePreview(null);
    if (!canManagePolicyAdminEvidence) {
      setSuiteError("Admin role required to run saved CRM policy suites.");
      return;
    }
    if (!suiteFixtures?.fixtures) {
      setSuiteError("No saved CRM policy suite fixtures are available for this tenant.");
      return;
    }
    if (suiteFixtures.policyConfigDigestMatchesCurrent !== true) {
      setSuiteError("Saved CRM policy suite fixtures are stale or unbound. Reload the registry, then resave fixtures against the current policy digest before using saved-suite evidence.");
      return;
    }

    const policy = policyDirty ? parsePolicyPayload(policyText) : null;
    if (policy && !policy.ok) {
      setSuiteError(policy.error);
      return;
    }

    setIsPreviewingSuite(true);
    try {
      const response = await apiFetch<{ data: CrmProgramPolicyPreviewSuiteResponse }>("/crm/intelligence/program-config/preview-suite", {
        method: "POST",
        body: JSON.stringify(policy?.ok ? { useSavedFixtures: true, config: policy.value } : { useSavedFixtures: true }),
      });
      const boundaryError = getPreviewSuiteBoundaryError(response.data);
      if (boundaryError) {
        setSuiteError(boundaryError);
        return;
      }
      setSuitePreview(response.data);
    } catch (error) {
      setSuiteError(error instanceof Error ? error.message : "Failed to run saved CRM policy suite.");
    } finally {
      setIsPreviewingSuite(false);
    }
  }

  async function loadPreviewSuiteFixtures() {
    setSuiteError(null);
    setSuiteRegistryMessage(null);
    if (!canManagePolicyAdminEvidence) {
      setSuiteError("Admin role required to load saved CRM policy suite fixtures.");
      return;
    }
    setIsLoadingSuiteFixtures(true);
    try {
      const response = await apiFetch<{ data: CrmProgramPreviewSuiteFixturesResponse }>("/crm/intelligence/program-config/preview-suite-fixtures", {
        method: "GET",
      });
      setSuiteFixtures(response.data);
      if (response.data.fixtures) {
        setSuiteText(formatPreviewSuiteFixtures(response.data.fixtures));
        setSuiteRegistryMessage("Saved fixture suite loaded into the editor.");
      } else {
        setSuiteRegistryMessage("No saved fixture suite exists for this tenant yet.");
      }
    } catch (error) {
      setSuiteError(error instanceof Error ? error.message : "Failed to load CRM policy suite fixtures.");
    } finally {
      setIsLoadingSuiteFixtures(false);
    }
  }

  async function savePreviewSuiteFixtures() {
    setSuiteError(null);
    setSuiteRegistryMessage(null);

    if (!canManagePolicyAdminEvidence) {
      setSuiteError("Admin role required to save CRM policy suite fixtures.");
      return;
    }

    const suite = parsePreviewSuitePayload(suiteText);
    if (!suite.ok) {
      setSuiteError(suite.error);
      return;
    }

    setIsSavingSuiteFixtures(true);
    try {
      const response = await apiFetch<{ data: CrmProgramPreviewSuiteFixturesResponse }>("/crm/intelligence/program-config/preview-suite-fixtures", {
        method: "PATCH",
        body: JSON.stringify(suite.value),
      });
      setSuiteFixtures(response.data);
      if (response.data.fixtures) {
        setSuiteText(formatPreviewSuiteFixtures(response.data.fixtures));
      }
      setSuiteRegistryMessage("Fixture suite saved on the tenant CRM policy seam.");
    } catch (error) {
      setSuiteError(error instanceof Error ? error.message : "Failed to save CRM policy suite fixtures.");
    } finally {
      setIsSavingSuiteFixtures(false);
    }
  }

  async function savePolicy() {
    setPreviewError(null);
    setReviewError(null);
    setSaveMessage(null);
    if (!canManagePolicyAdminEvidence) {
      setSaveMessage("Admin role required to save tenant CRM policy.");
      return;
    }
    const policy = parsePolicyPayload(policyText);
    if (!policy.ok) {
      setSaveMessage(policy.error);
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiFetch<{ data: CrmProgramConfigResponse }>("/crm/intelligence/program-config", {
        method: "PATCH",
        body: JSON.stringify(policy.value),
      });
      setConfig(response.data);
      setPolicyText(formatPolicyPayload(response.data));
      setPolicyDirty(false);
      setPolicyReview(null);
      setPolicyAudit(null);
      setPolicyReadiness(null);
      setSuitePreview(null);
      setSuiteFixtures(null);
      setSuiteRegistryMessage("Policy saved. Reload or resave fixture suite before running saved-suite evidence.");
      setSaveMessage("Policy saved on the tenant CRM config seam.");
    } catch (error) {
      setSaveMessage(error instanceof Error ? error.message : "Failed to save CRM policy.");
    } finally {
      setIsSaving(false);
    }
  }

  async function loadPolicyAudit() {
    setAuditError(null);
    if (!canManagePolicyAdminEvidence) {
      setAuditError("Admin role required to load CRM policy audit evidence.");
      return;
    }
    setIsLoadingAudit(true);
    try {
      const response = await apiFetch<{ data: CrmProgramPolicyAuditResponse }>("/crm/intelligence/program-config/audit", {
        method: "GET",
      });
      setPolicyAudit(response.data);
    } catch (error) {
      setAuditError(error instanceof Error ? error.message : "Failed to load CRM policy audit.");
    } finally {
      setIsLoadingAudit(false);
    }
  }

  async function loadPolicyReadiness() {
    setReadinessError(null);
    if (!canManagePolicyAdminEvidence) {
      setReadinessError("Admin role required to load CRM policy readiness evidence.");
      return;
    }
    setIsLoadingReadiness(true);
    try {
      const response = await apiFetch<{ data: CrmProgramPolicyReadinessResponse }>("/crm/intelligence/program-config/readiness", {
        method: "GET",
      });
      setPolicyReadiness(response.data);
    } catch (error) {
      setReadinessError(error instanceof Error ? error.message : "Failed to load CRM policy readiness.");
    } finally {
      setIsLoadingReadiness(false);
    }
  }

  async function reviewPolicy() {
    setReviewError(null);
    setSaveMessage(null);
    if (!canManagePolicyAdminEvidence) {
      setReviewError("Admin role required to review CRM policy changes.");
      return;
    }
    const policy = policyDirty ? parsePolicyPayload(policyText) : null;
    if (policy && !policy.ok) {
      setReviewError(policy.error);
      return;
    }

    setIsReviewing(true);
    try {
      const response = await apiFetch<{ data: CrmProgramPolicyReviewResponse }>("/crm/intelligence/program-config/review", {
        method: "POST",
        body: JSON.stringify(policy?.ok ? { config: policy.value } : {}),
      });
      setPolicyReview(response.data);
    } catch (error) {
      setReviewError(error instanceof Error ? error.message : "Failed to review CRM policy.");
    } finally {
      setIsReviewing(false);
    }
  }

  function resetPolicyDraft() {
    if (!config) return;
    setPolicyText(formatPolicyPayload(config));
    setPolicyDirty(false);
    setPolicyReview(null);
    setPolicyAudit(null);
    setPolicyReadiness(null);
    setReviewError(null);
    setAuditError(null);
    setReadinessError(null);
    setSaveMessage(null);
    setPreviewError(null);
    setSuiteError(null);
    setSuitePreview(null);
  }

  if (isLoading) {
    return (
      <main className="mx-auto max-w-6xl space-y-6 p-6" aria-busy="true">
        <div className="h-10 w-72 animate-pulse rounded-md bg-[var(--color-muted,#e5e7eb)]" />
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="h-36 animate-pulse rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)]" />
          ))}
        </div>
      </main>
    );
  }

  if (loadError || !config) {
    return (
      <main className="mx-auto max-w-6xl p-6">
        <div className="rounded-2xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-5 text-sm text-[var(--color-destructive)]">
          Failed to load the CRM policy workbench. {loadError}
        </div>
      </main>
    );
  }

  const savedSuiteCanRun = suiteFixtures?.fixtures
    ? suiteFixtures.policyConfigDigestMatchesCurrent === true
    : false;
  const qualificationGates = config.qualificationGates ?? [];
  const scheduledTaskRules = config.scheduledTaskRules ?? [];

  return (
    <main className="mx-auto max-w-6xl space-y-6 p-6">
      <header className="rounded-3xl border border-[var(--color-border)] bg-[var(--color-card)] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-primary)]">
              Tenant CRM policy
            </p>
            <h1 className="mt-2 text-3xl font-bold text-[var(--color-foreground)]">Policy Workbench</h1>
            <p className="mt-2 max-w-2xl text-sm text-[var(--color-muted-foreground)]">
              Inspect the reusable scoring, intent, task, and projection policy for this tenant. Dry-run previews
              classify facts without writing CRM records, dispatching tasks, enabling serving, or advancing cutover.
            </p>
          </div>
          <div className="rounded-2xl border border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 px-4 py-3 text-sm">
            <p className="font-semibold text-[var(--color-primary)]">{config.source === "saved" ? "Saved policy" : "Default policy"}</p>
            <p className="text-[var(--color-muted-foreground)]">{config.programVersion}</p>
          </div>
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <SummaryCard label="Program" value={config.programKey} detail={`${config.productAppKey} / ${config.tenantSlug}`} />
        <SummaryCard label="Score range" value={`${config.scoreRange.min}-${config.scoreRange.max}`} detail={`${config.scoreBands.length} bands`} />
        <SummaryCard label="Signals" value={String(signalCount(config))} detail={`${config.scoreComponents.length} components / ${qualificationGates.length} gates`} />
        <SummaryCard label="Actions" value={String(config.taskRules.length + scheduledTaskRules.length)} detail={`${config.taskRules.length} intent / ${scheduledTaskRules.length} scheduled`} />
        <SummaryCard label="Safety" value="Dry-run only" detail="No target writes or serving changes" />
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="space-y-6">
          <Panel title="Score Bands" description="Ordered thresholds used after component scores are summed.">
            <div className="grid gap-3 sm:grid-cols-2">
              {config.scoreBands.map((band) => (
                <div key={band.key} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-[var(--color-foreground)]">{band.label}</p>
                    <span className="rounded-full bg-[var(--color-primary-light)] px-2 py-1 text-xs font-semibold text-[var(--color-primary)]">
                      {band.minScore}+
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">{band.key}</p>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Scoring Signals" description="Reusable components and fact keys. The workbench shows keys and rules, not raw lead payloads.">
            <div className="space-y-4">
              {config.scoreComponents.map((component) => (
                <article key={component.key} className="rounded-2xl border border-[var(--color-border)] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-[var(--color-foreground)]">{component.label}</h3>
                      <p className="text-xs text-[var(--color-muted-foreground)]">{component.key}</p>
                    </div>
                    <span className="rounded-full border border-[var(--color-border)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                      Max {component.maxScore}
                    </span>
                  </div>
                  <div className="mt-4 space-y-3">
                    {component.signals.map((signal) => (
                      <div key={signal.key} className="rounded-xl bg-[var(--color-surface)] p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-medium text-[var(--color-foreground)]">{signal.label}</p>
                          <span className="text-xs font-semibold text-[var(--color-primary)]">+{signal.points}</span>
                        </div>
                        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{signal.description}</p>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-[var(--color-muted-foreground)]">
                          <code className="rounded bg-[var(--color-card)] px-2 py-1">fact:{signal.factKey}</code>
                          <code className="rounded bg-[var(--color-card)] px-2 py-1">reason:{signal.reasonCode}</code>
                          {signal.condition ? <code className="rounded bg-[var(--color-card)] px-2 py-1">{formatCondition(signal.condition)}</code> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </Panel>
        </div>

        <div className="space-y-6">
          <Panel title="Intent And Actions" description="How the score becomes intent and non-serving task recommendations.">
            <div className="space-y-3">
              {config.intentRules.map((rule) => (
                <div key={rule.key} className="rounded-xl border border-[var(--color-border)] p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-[var(--color-foreground)]">{rule.label}</p>
                    <span className="rounded-full bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                      priority {rule.priority}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{rule.when}</p>
                  <p className="mt-2 text-xs text-[var(--color-primary)]">intent: {rule.intentClass}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-3 border-t border-[var(--color-border)] pt-5">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Qualification gates</h3>
              {qualificationGates.length > 0 ? qualificationGates.map((gate) => (
                <div key={gate.key} className="rounded-xl bg-[var(--color-surface)] p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-[var(--color-foreground)]">{gate.label}</p>
                    <span className="rounded-full bg-[var(--color-card)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                      {gate.effect}{gate.maxScore !== undefined ? ` <= ${gate.maxScore}` : ""}
                    </span>
                  </div>
                  <p className="mt-1 text-[var(--color-muted-foreground)]">{gate.when}</p>
                  {gate.reasonCode ? (
                    <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">reason: {gate.reasonCode}</p>
                  ) : null}
                  {gate.condition ? (
                    <p className="mt-1 text-xs text-[var(--color-primary)]">{formatCondition(gate.condition)}</p>
                  ) : null}
                </div>
              )) : (
                <p className="rounded-xl bg-[var(--color-surface)] p-3 text-sm text-[var(--color-muted-foreground)]">
                  No qualification gates configured for this tenant policy.
                </p>
              )}
            </div>
            <div className="mt-5 space-y-3 border-t border-[var(--color-border)] pt-5">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Task rules</h3>
              {config.taskRules.map((rule) => (
                <div key={rule.key} className="rounded-xl bg-[var(--color-surface)] p-3 text-sm">
                  <p className="font-medium text-[var(--color-foreground)]">{rule.title}</p>
                  <p className="mt-1 text-[var(--color-muted-foreground)]">
                    {rule.triggerIntent} {"->"} {rule.actionRef} / {rule.templateKey} in {rule.dueInHours}h
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-3 border-t border-[var(--color-border)] pt-5">
              <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Scheduled task rules</h3>
              {scheduledTaskRules.length > 0 ? scheduledTaskRules.map((rule) => (
                <div key={rule.key} className="rounded-xl bg-[var(--color-surface)] p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-[var(--color-foreground)]">{rule.title}</p>
                    <span className="rounded-full bg-[var(--color-card)] px-2 py-1 text-xs text-[var(--color-muted-foreground)]">
                      {rule.priority} / due {rule.dueDays}d
                    </span>
                  </div>
                  <p className="mt-1 text-[var(--color-muted-foreground)]">{rule.description}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-[var(--color-muted-foreground)]">
                    <code className="rounded bg-[var(--color-card)] px-2 py-1">type:{rule.taskType}</code>
                    <code className="rounded bg-[var(--color-card)] px-2 py-1">reason:{rule.reasonCode}</code>
                    <code className="rounded bg-[var(--color-card)] px-2 py-1">{formatCondition(rule.condition)}</code>
                  </div>
                  {rule.nextAction ? (
                    <p className="mt-2 text-xs text-[var(--color-primary)]">next action: {rule.nextAction}</p>
                  ) : null}
                </div>
              )) : (
                <p className="rounded-xl bg-[var(--color-surface)] p-3 text-sm text-[var(--color-muted-foreground)]">
                  No scheduled task rules configured for this tenant policy.
                </p>
              )}
            </div>
          </Panel>

          <Panel title="Projection Boundary" description="Target fields that a reviewed executor may later write. Preview mode only lists descriptors.">
            <div className="space-y-2">
              {config.personProjectionRules.map((rule) => (
                <div key={rule.key} className="flex items-center justify-between gap-3 rounded-xl bg-[var(--color-surface)] px-3 py-2 text-sm">
                  <div>
                    <p className="font-medium text-[var(--color-foreground)]">{rule.targetField}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">{rule.source}</p>
                  </div>
                  <span className={rule.piiSafe ? "text-xs font-semibold text-[var(--color-success)]" : "text-xs font-semibold text-[var(--color-destructive)]"}>
                    {rule.piiSafe ? "PII-safe" : "Blocked"}
                  </span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Policy JSON" description="Edit the reusable tenant policy payload. Save uses the mounted admin PATCH route and never changes serving or cutover state.">
            {!canManagePolicyAdminEvidence ? (
              <p className="mb-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-3 text-sm text-[var(--color-muted-foreground)]">
                Viewer mode: scoring, intent, tasks, and projection policy are visible, but admin evidence actions are disabled for this role.
              </p>
            ) : null}
            <textarea
              value={policyText}
              onChange={(event) => {
                setPolicyText(event.target.value);
                setPolicyDirty(true);
                setSaveMessage(null);
                setPolicyReview(null);
                setPolicyAudit(null);
                setPolicyReadiness(null);
                setSuitePreview(null);
                setReviewError(null);
                setAuditError(null);
                setReadinessError(null);
                setSuiteError(null);
                setSuiteRegistryMessage(null);
              }}
              className="min-h-72 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 font-mono text-xs text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
              aria-label="CRM policy config JSON"
              spellCheck={false}
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void loadPolicyAudit()}
                disabled={!canManagePolicyAdminEvidence || isLoadingAudit || isLoadingReadiness || isReviewing || isSaving}
                className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-semibold text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoadingAudit ? "Loading audit..." : "Load audit evidence"}
              </button>
              <button
                type="button"
                onClick={() => void loadPolicyReadiness()}
                disabled={!canManagePolicyAdminEvidence || isLoadingReadiness || isLoadingAudit || isReviewing || isSaving || policyDirty}
                className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-semibold text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoadingReadiness ? "Loading readiness..." : "Load readiness gate"}
              </button>
              <button
                type="button"
                onClick={() => void reviewPolicy()}
                disabled={!canManagePolicyAdminEvidence || isReviewing || isSaving || isLoadingAudit || isLoadingReadiness}
                className="rounded-lg border border-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isReviewing ? "Reviewing..." : policyDirty ? "Review draft" : "Review saved policy"}
              </button>
              <button
                type="button"
                onClick={() => void savePolicy()}
                disabled={!canManagePolicyAdminEvidence || isSaving || isReviewing || isLoadingAudit || isLoadingReadiness || !policyDirty}
                className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Save tenant policy"}
              </button>
              <button
                type="button"
                onClick={resetPolicyDraft}
                disabled={!policyDirty || isSaving || isReviewing || isLoadingAudit || isLoadingReadiness}
                className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-semibold text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Reset draft
              </button>
              {policyDirty ? (
                <span className="text-xs font-medium text-[var(--color-primary)]">Draft not saved</span>
              ) : null}
            </div>
            {saveMessage ? (
              <p className={`mt-3 rounded-xl border p-3 text-sm ${
                saveMessage.startsWith("Policy saved")
                  ? "border-[var(--color-success)]/30 bg-[var(--color-success)]/5 text-[var(--color-success)]"
                  : "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 text-[var(--color-destructive)]"
              }`}>
                {saveMessage}
              </p>
            ) : null}
            {reviewError ? (
              <p className="mt-3 rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
                {reviewError}
              </p>
            ) : null}
            {auditError ? (
              <p className="mt-3 rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
                {auditError}
              </p>
            ) : null}
            {readinessError ? (
              <p className="mt-3 rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
                {readinessError}
              </p>
            ) : null}
            {policyAudit ? <PolicyAuditResult audit={policyAudit} /> : null}
            {policyReadiness ? <PolicyReadinessResult readiness={policyReadiness} /> : null}
            {policyReview ? <PolicyReviewResult review={policyReview} /> : null}
          </Panel>

          <Panel title="Dry-Run Preview" description="Enter sanitized fact keys and values. The API response returns fact keys only.">
            <form onSubmit={previewPolicy} className="space-y-3">
              <textarea
                value={factsText}
                onChange={(event) => setFactsText(event.target.value)}
                className="min-h-44 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 font-mono text-xs text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                aria-label="CRM policy preview facts JSON"
                spellCheck={false}
              />
              <button
                type="submit"
                disabled={!canManagePolicyAdminEvidence || isPreviewing}
                className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isPreviewing ? "Previewing..." : policyDirty ? "Preview draft policy" : "Run dry-run preview"}
              </button>
            </form>
            {previewError ? (
              <div className="mt-4 rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
                {previewError}
              </div>
            ) : null}
            {preview ? <PreviewResult preview={preview} /> : null}
          </Panel>

          <Panel
            title="Fixture Suite"
            description="Run multiple sanitized cases to prove policy coverage and expected routing before tenant promotion."
          >
            {suiteFixtures ? (
              <SuiteFixturesSummary fixturesResponse={suiteFixtures} />
            ) : (
              <p
                data-policy-suite-fixtures-status
                className="mb-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]/70 p-3 text-sm text-[var(--color-muted-foreground)]"
              >
                Saved fixture registry is not loaded. Use Load saved suite when managing admin evidence.
              </p>
            )}
            <form onSubmit={previewPolicySuite} className="space-y-3">
              <textarea
                value={suiteText}
                onChange={(event) => {
                  setSuiteText(event.target.value);
                  setSuiteRegistryMessage(null);
                }}
                className="min-h-56 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 font-mono text-xs text-[var(--color-foreground)] outline-none focus:border-[var(--color-primary)]"
                aria-label="CRM policy preview suite JSON"
                spellCheck={false}
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="submit"
                  disabled={!canManagePolicyAdminEvidence || isPreviewingSuite || isLoadingSuiteFixtures || isSavingSuiteFixtures}
                  className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isPreviewingSuite ? "Running suite..." : policyDirty ? "Run draft suite" : "Run preview suite"}
                </button>
                <button
                  type="button"
                  onClick={() => void previewSavedPolicySuite()}
                  disabled={!canManagePolicyAdminEvidence || isPreviewingSuite || isLoadingSuiteFixtures || isSavingSuiteFixtures || !savedSuiteCanRun}
                  className="rounded-lg border border-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isPreviewingSuite ? "Running..." : "Run saved suite"}
                </button>
                <button
                  type="button"
                  onClick={() => void loadPreviewSuiteFixtures()}
                  disabled={!canManagePolicyAdminEvidence || isPreviewingSuite || isLoadingSuiteFixtures || isSavingSuiteFixtures}
                  className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-semibold text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLoadingSuiteFixtures ? "Loading..." : "Load saved suite"}
                </button>
                <button
                  type="button"
                  onClick={() => void savePreviewSuiteFixtures()}
                  disabled={!canManagePolicyAdminEvidence || isPreviewingSuite || isLoadingSuiteFixtures || isSavingSuiteFixtures}
                  className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-semibold text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingSuiteFixtures ? "Saving..." : "Save suite fixtures"}
                </button>
              </div>
            </form>
            {suiteRegistryMessage ? (
              <p className="mt-4 rounded-xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/5 p-3 text-sm text-[var(--color-success)]">
                {suiteRegistryMessage}
              </p>
            ) : null}
            {suiteError ? (
              <div className="mt-4 rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-3 text-sm text-[var(--color-destructive)]">
                {suiteError}
              </div>
            ) : null}
            {suitePreview ? <PreviewSuiteResult suitePreview={suitePreview} /> : null}
          </Panel>
        </div>
      </section>
    </main>
  );
}

function SummaryCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <p className="text-xs font-medium uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">{label}</p>
      <p className="mt-2 truncate text-xl font-bold text-[var(--color-foreground)]" title={value}>{value}</p>
      <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{detail}</p>
    </div>
  );
}

function Panel({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{title}</h2>
        <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{description}</p>
      </div>
      {children}
    </section>
  );
}

function PreviewResult({ preview }: { preview: CrmProgramPolicyPreviewResponse }) {
  const qualificationGateResults = preview.decision.qualificationGateResults ?? [];
  const matchedQualificationGateKeys = getMatchedQualificationGateKeys(preview.previewEvidence);
  const matchedQualificationGateReasonCodes = getMatchedQualificationGateReasonCodes(preview.previewEvidence);
  const matchedQualificationGateCount = getMatchedQualificationGateCount(preview.previewEvidence);
  const scheduledTaskRecommendations = preview.scheduledTaskRecommendations ?? [];

  return (
    <div
      data-policy-preview-result
      className="mt-5 space-y-4 rounded-2xl border border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 p-4"
    >
      <div className="grid gap-3 sm:grid-cols-5">
        <SummaryCard label="Score" value={String(preview.decision.score)} detail={`pre-gate ${preview.decision.scoreBeforeQualification}; band ${preview.decision.scoreBand}`} />
        <SummaryCard label="Intent" value={preview.decision.intentClass} detail={`rule ${preview.decision.matchedIntentRuleKey}`} />
        <SummaryCard label="Gate" value={preview.decision.qualificationGate} detail={`${qualificationGateResults.filter((gate) => gate.matched).length} matched`} />
        <SummaryCard label="Scheduled" value={String(scheduledTaskRecommendations.length)} detail={preview.scheduledPreviewNow ? `dry-run ${preview.scheduledPreviewNow}` : "dry-run"} />
        <SummaryCard label="Boundary" value={preview.targetWritesExecuted ? "Writes ran" : "No writes"} detail={preview.servingDecision} />
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3 text-sm">
        <p className="font-semibold text-[var(--color-foreground)]">Fact keys evaluated</p>
        <p className="mt-1 text-[var(--color-muted-foreground)]">{preview.factKeys.length > 0 ? preview.factKeys.join(", ") : "None"}</p>
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          rawFactsVisible={String(preview.rawFactsVisible)} targetWritesExecuted={String(preview.targetWritesExecuted)} cutover={preview.cutoverDecision}
        </p>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3 text-sm">
        <p className="font-semibold text-[var(--color-foreground)]">Configured fact coverage</p>
        <div className="mt-2 grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-3">
          <p>configured: {preview.factCoverage.configuredFactKeys.length}</p>
          <p>missing: {preview.factCoverage.missingConfiguredFactKeys.length}</p>
          <p>unused supplied: {preview.factCoverage.unusedSuppliedFactKeys.length}</p>
        </div>
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Configured keys: {formatFactKeyList(preview.factCoverage.configuredFactKeys)}
        </p>
        <p className="mt-1 text-xs text-[var(--color-warning)]">
          Missing configured fact keys: {formatFactKeyList(preview.factCoverage.missingConfiguredFactKeys)}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          Unused supplied fact keys: {formatFactKeyList(preview.factCoverage.unusedSuppliedFactKeys)}
        </p>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3 text-sm">
        <p className="font-semibold text-[var(--color-foreground)]">Operator preview evidence</p>
        <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">
          policy config: {preview.configDigestSha256}
        </p>
        <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">
          digest: {preview.previewEvidenceDigestSha256}
        </p>
        <div className="mt-2 grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-5">
          <p>coverage: {preview.previewEvidence.coverageStatus}</p>
          <p>signals: {preview.previewEvidence.counts.matchedSignals}</p>
          <p>gates: {matchedQualificationGateCount}</p>
          <p>tasks: {preview.previewEvidence.counts.taskRecommendations}</p>
          <p>scheduled: {preview.previewEvidence.counts.scheduledTaskRecommendations ?? scheduledTaskRecommendations.length}</p>
        </div>
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          matched signals: {formatFactKeyList(preview.previewEvidence.matchedSignalKeys)}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          matched gates: {formatFactKeyList(matchedQualificationGateKeys)}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          matched gate reasons: {formatFactKeyList(matchedQualificationGateReasonCodes)}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          task keys: {formatFactKeyList(preview.previewEvidence.taskRecommendationKeys)}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          scheduled task keys: {formatFactKeyList(preview.previewEvidence.scheduledTaskRecommendationKeys ?? scheduledTaskRecommendations.map((task) => task.key))}
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl bg-[var(--color-card)] p-3">
          <p className="text-sm font-semibold text-[var(--color-foreground)]">Component scores</p>
          <div className="mt-2 space-y-1 text-sm text-[var(--color-muted-foreground)]">
            {Object.entries(preview.decision.componentScores).map(([key, value]) => (
              <p key={key}>{key}: {value}</p>
            ))}
          </div>
        </div>
        <div className="rounded-xl bg-[var(--color-card)] p-3">
          <p className="text-sm font-semibold text-[var(--color-foreground)]">Reason codes</p>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            {preview.decision.reasonCodes.length > 0 ? preview.decision.reasonCodes.join(", ") : "No reasons matched"}
          </p>
        </div>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3">
        <p className="text-sm font-semibold text-[var(--color-foreground)]">Matched signal points</p>
        <div className="mt-2 space-y-1 text-sm text-[var(--color-muted-foreground)]">
          {preview.decision.signalScores.length > 0 ? preview.decision.signalScores.map((signal) => (
            <p key={`${signal.componentKey}-${signal.signalKey}-${signal.reasonCode}`}>
              {signal.componentKey}.{signal.signalKey} +{signal.points} ({signal.reasonCode})
            </p>
          )) : (
            <p>No scoring signals matched this preview.</p>
          )}
        </div>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3">
        <p className="text-sm font-semibold text-[var(--color-foreground)]">Qualification gates</p>
        <div className="mt-2 space-y-1 text-sm text-[var(--color-muted-foreground)]">
          {qualificationGateResults.length > 0 ? qualificationGateResults.map((gate) => (
            <p key={gate.key}>
              {gate.key}: {gate.matched ? "matched" : "not matched"} / {gate.effect}
              {gate.maxScore !== null ? ` <= ${gate.maxScore}` : ""}
              {gate.appliedCapScore !== null ? `, applied ${gate.appliedCapScore}` : ""}
              {gate.reasonCode ? `; reason ${gate.reasonCode}` : ""}
            </p>
          )) : (
            <p>No qualification gates configured.</p>
          )}
        </div>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3">
        <p className="text-sm font-semibold text-[var(--color-foreground)]">Task recommendations</p>
        <div className="mt-2 space-y-2">
          {preview.taskRecommendations.length > 0 ? preview.taskRecommendations.map((task) => (
            <div key={task.key} className="rounded-lg border border-[var(--color-border)] p-2 text-sm text-[var(--color-muted-foreground)]">
              <p className="font-medium text-[var(--color-foreground)]">{task.title} {"->"} {task.actionRef}</p>
              <p>({task.templateKey}, {task.dueInHours}h)</p>
              {(task.taskType || task.priority || task.nextAction || task.hasScriptTemplate) && (
                <p className="mt-1 text-xs">
                  {[task.taskType, task.priority, task.nextAction, task.hasScriptTemplate ? "script" : null].filter(Boolean).join(" / ")}
                </p>
              )}
              {task.description && <p className="mt-1 text-xs">{task.description}</p>}
            </div>
          )) : (
            <p className="text-sm text-[var(--color-muted-foreground)]">No task rule matched this intent.</p>
          )}
        </div>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3" data-policy-scheduled-preview>
        <p className="text-sm font-semibold text-[var(--color-foreground)]">Scheduled task dry-run</p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          Evaluates scheduled task policy only. No task rows are created from this preview.
        </p>
        <div className="mt-2 space-y-2">
          {scheduledTaskRecommendations.length > 0 ? scheduledTaskRecommendations.map((task) => (
            <div key={task.key} className="rounded-lg border border-[var(--color-border)] p-2 text-sm text-[var(--color-muted-foreground)]">
              <p className="font-medium text-[var(--color-foreground)]">{task.title}</p>
              <p>{task.taskType} / {task.priority} / due {task.dueDays}d</p>
              <p className="text-xs">reason {task.reasonCode}; due at {task.dueAt}</p>
              {task.nextAction ? <p className="mt-1 text-xs">next action: {task.nextAction}</p> : null}
              {task.description ? <p className="mt-1 text-xs">{task.description}</p> : null}
            </div>
          )) : (
            <p className="text-sm text-[var(--color-muted-foreground)]">No scheduled task rule matched these facts.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewSuiteResult({ suitePreview }: { suitePreview: CrmProgramPolicyPreviewSuiteResponse }) {
  const { suite } = suitePreview;
  return (
    <div
      data-policy-preview-suite-result
      className="mt-5 space-y-4 rounded-2xl border border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 p-4"
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <SummaryCard label="Suite" value={suite.status} detail={`${suite.counts.cases} cases`} />
        <SummaryCard label="Mismatches" value={String(suite.counts.expectationMismatches)} detail={`${suite.counts.casesWithExpectations} expected`} />
        <SummaryCard label="Boundary" value={suite.targetWritesExecuted ? "Writes ran" : "No writes"} detail={suite.servingDecision} />
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3 text-sm">
        <p className="font-semibold text-[var(--color-foreground)]">Suite evidence</p>
        {suitePreview.fixtureSource ? (
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            fixture source: {suitePreview.fixtureSource}
          </p>
        ) : null}
        <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">
          policy config: {suitePreview.configDigestSha256}
        </p>
        {suitePreview.fixtureSuiteDigestSha256 ? (
          <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">
            saved fixture suite: {suitePreview.fixtureSuiteDigestSha256}
          </p>
        ) : null}
        {suitePreview.fixturePolicyConfigDigestSha256 ? (
          <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">
            saved fixture policy config: {suitePreview.fixturePolicyConfigDigestSha256}
          </p>
        ) : null}
        {typeof suitePreview.fixturePolicyConfigDigestMatchesPreviewConfig === "boolean" ? (
          <p className={`mt-1 text-xs ${
            suitePreview.fixturePolicyConfigDigestMatchesPreviewConfig
              ? "text-[var(--color-success)]"
              : "text-[var(--color-warning)]"
          }`}>
            saved fixture policy match: {String(suitePreview.fixturePolicyConfigDigestMatchesPreviewConfig)}
          </p>
        ) : null}
        <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">
          digest: {suitePreview.suiteDigestSha256}
        </p>
        <div className="mt-2 grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-4">
          <p>configured: {suite.aggregateFactCoverage.configuredFactKeys.length}</p>
          <p>missing: {suite.aggregateFactCoverage.missingConfiguredFactKeys.length}</p>
          <p>unused supplied: {suite.aggregateFactCoverage.unusedSuppliedFactKeys.length}</p>
          <p>matched gates: {suite.counts.matchedQualificationGates ?? 0}</p>
          <p>matched gate reasons: {suite.counts.matchedQualificationGateReasonCodes ?? 0}</p>
        </div>
        <p className="mt-2 text-xs text-[var(--color-warning)]">
          Missing configured fact keys: {formatFactKeyList(suite.aggregateFactCoverage.missingConfiguredFactKeys)}
        </p>
        <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
          Unused supplied fact keys: {formatFactKeyList(suite.aggregateFactCoverage.unusedSuppliedFactKeys)}
        </p>
      </div>
      <div className="space-y-2">
        {suite.cases.map((previewCase) => (
          <div key={previewCase.key} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-[var(--color-foreground)]">
                {previewCase.label ?? previewCase.key}
              </p>
              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                previewCase.expectationStatus === "mismatched"
                  ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                  : "bg-[var(--color-success)]/10 text-[var(--color-success)]"
              }`}>
                {previewCase.expectationStatus}
              </span>
            </div>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              score {previewCase.previewEvidence.decisionSummary.score} (pre-gate {previewCase.previewEvidence.decisionSummary.scoreBeforeQualification}) / {previewCase.previewEvidence.decisionSummary.scoreBand}; intent {previewCase.previewEvidence.decisionSummary.intentClass}; gate {previewCase.previewEvidence.decisionSummary.qualificationGate}
            </p>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              matched gates: {formatFactKeyList(getMatchedQualificationGateKeys(previewCase.previewEvidence))}
            </p>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              matched gate reasons: {formatFactKeyList(getMatchedQualificationGateReasonCodes(previewCase.previewEvidence))}
            </p>
            <p className="mt-1 break-all text-xs text-[var(--color-muted-foreground)]">
              evidence {previewCase.previewEvidenceDigestSha256}
            </p>
            {previewCase.expectationMismatches.length > 0 ? (
              <p className="mt-1 text-xs text-[var(--color-warning)]">
                {previewCase.expectationMismatches.join("; ")}
              </p>
            ) : null}
          </div>
        ))}
      </div>
      <p className="text-xs text-[var(--color-muted-foreground)]">
        rawFactsVisible={String(suite.rawFactsVisible)} targetWritesExecuted={String(suite.targetWritesExecuted)} cutover={suite.cutoverDecision}
      </p>
    </div>
  );
}

function SuiteFixturesSummary({ fixturesResponse }: { fixturesResponse: CrmProgramPreviewSuiteFixturesResponse }) {
  const fixtures = fixturesResponse.fixtures;
  const bindingStatus = fixturesResponse.policyConfigDigestMatchesCurrent === null
    ? "no saved fixture binding"
    : fixturesResponse.policyConfigDigestMatchesCurrent
      ? "matches current policy"
      : "stale or unbound";
  const bindingClassName = fixturesResponse.policyConfigDigestMatchesCurrent
    ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
    : fixturesResponse.policyConfigDigestMatchesCurrent === false
      ? "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
      : "bg-[var(--color-primary-light)] text-[var(--color-primary)]";
  return (
    <div
      data-policy-suite-fixtures-summary
      className="mb-4 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-[var(--color-foreground)]">
            {fixtures ? fixtures.suiteKey : "No saved fixture suite"}
          </p>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {fixturesResponse.source} / {fixtures?.suiteVersion ?? "not configured"}
          </p>
        </div>
        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${bindingClassName}`}>
          {fixtures ? bindingStatus : "empty"}
        </span>
      </div>
      {fixtures ? (
        <div className="mt-2 space-y-1 text-xs text-[var(--color-muted-foreground)]">
          <p className="break-all">digest: {fixtures.digestSha256}</p>
          <p className="break-all">policy config: {fixtures.policyConfigDigestSha256 ?? "unbound"}</p>
          <p className="break-all">current policy config: {fixturesResponse.currentPolicyConfigDigestSha256}</p>
          <p>policy binding: {bindingStatus}</p>
          <p>expected cases: {fixtures.counts.casesWithExpectations}; supplied fact keys: {fixtures.counts.suppliedFactKeys}</p>
          <p>
            saved: {fixturesResponse.saved ? `${fixturesResponse.saved.updatedAt} by ${fixturesResponse.saved.updatedBy}` : "pending persisted audit metadata"}
          </p>
          {fixturesResponse.policyConfigDigestMatchesCurrent === false ? (
            <p className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5 p-2 text-[var(--color-warning)]">
              Resave fixtures before using the saved-suite run as shadow evidence for the current policy.
            </p>
          ) : null}
          <p>
            rawFactsVisible={String(fixtures.rawFactsVisible)} targetWritesExecuted={String(fixtures.targetWritesExecuted)} cutover={fixtures.cutoverDecision}
          </p>
        </div>
      ) : (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Save sanitized expected cases here so the same tenant policy suite can be re-run by operators and CI.
        </p>
      )}
    </div>
  );
}

function PolicyReviewResult({ review }: { review: CrmProgramPolicyReviewResponse }) {
  return (
    <div
      data-policy-review-result
      className="mt-4 space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-[var(--color-foreground)]">Policy review</p>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {review.source} / {review.version}
          </p>
        </div>
        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${reviewStatusClass(review.status)}`}>
          {review.status}
        </span>
      </div>
      <div className="grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-5">
        <p>signals: {review.counts.scoreSignals}</p>
        <p>gates: {review.counts.qualificationGates}</p>
        <p>tasks: {review.counts.taskRules}</p>
        <p>scheduled: {review.counts.scheduledTaskRules}</p>
        <p>projections: {review.counts.projectionRules}</p>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3 text-xs text-[var(--color-muted-foreground)]">
        <p>actions: {review.targetActionRefs.length > 0 ? review.targetActionRefs.join(", ") : "none"}</p>
        <p className="mt-1">
          targetWritesExecuted={String(review.targetWritesExecuted)} serving={review.servingDecision} cutover={review.cutoverDecision}
        </p>
      </div>
      <div className="space-y-2">
        {review.findings.length > 0 ? review.findings.map((finding) => (
          <div key={`${finding.code}-${finding.path ?? "policy"}`} className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm">
            <p className="font-semibold text-[var(--color-foreground)]">
              {finding.severity}: {finding.code}
            </p>
            <p className="mt-1 text-[var(--color-muted-foreground)]">{finding.message}</p>
            {finding.path ? <p className="mt-1 text-xs text-[var(--color-primary)]">{finding.path}</p> : null}
          </div>
        )) : (
          <p className="rounded-xl bg-[var(--color-card)] p-3 text-sm text-[var(--color-muted-foreground)]">
            No policy findings returned by the review route.
          </p>
        )}
      </div>
    </div>
  );
}

function PolicyAuditResult({ audit }: { audit: CrmProgramPolicyAuditResponse }) {
  return (
    <div
      data-policy-audit-result
      className="mt-4 space-y-3 rounded-2xl border border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-[var(--color-foreground)]">Policy audit evidence</p>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {audit.source} / {audit.programVersion}
          </p>
        </div>
        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${reviewStatusClass(audit.review.status)}`}>
          {audit.review.status}
        </span>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3 text-xs text-[var(--color-muted-foreground)]">
        <p className="break-all">digest: {audit.configDigestSha256}</p>
        <p className="mt-1">
          saved: {audit.saved ? `${audit.saved.updatedAt} by ${audit.saved.updatedBy}` : "default policy, no tenant save metadata"}
        </p>
        <p className="mt-1">
          targetWritesExecuted={String(audit.targetWritesExecuted)} serving={audit.servingDecision} cutover={audit.cutoverDecision}
        </p>
      </div>
      <div className="grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-5">
        <p>signals: {audit.review.counts.scoreSignals}</p>
        <p>gates: {audit.review.counts.qualificationGates}</p>
        <p>tasks: {audit.review.counts.taskRules}</p>
        <p>scheduled: {audit.review.counts.scheduledTaskRules}</p>
        <p>findings: {audit.review.findings.length}</p>
      </div>
    </div>
  );
}

function PolicyReadinessResult({ readiness }: { readiness: CrmProgramPolicyReadinessResponse }) {
  return (
    <div
      data-policy-readiness-result
      className="mt-4 space-y-3 rounded-2xl border border-[var(--color-primary)]/25 bg-[var(--color-primary)]/5 p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-[var(--color-foreground)]">Policy readiness gate</p>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {readiness.source} / {readiness.programVersion}
          </p>
        </div>
        <span className={`rounded-full px-2 py-1 text-xs font-semibold ${readinessStatusClass(readiness.status)}`}>
          {readiness.status}
        </span>
      </div>
      <div className="grid gap-2 text-xs text-[var(--color-muted-foreground)] sm:grid-cols-3">
        <p>policy: {readiness.policyReviewStatus}</p>
        <p>findings: {readiness.counts.policyFindings}</p>
        <p>action blockers: {readiness.counts.actionSmokeBlockers}</p>
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3 text-xs text-[var(--color-muted-foreground)]">
        <p className="break-all">policy config: {readiness.configDigestSha256}</p>
        <p>action smoke: {readiness.actionSmoke.decision}</p>
        <p className="mt-1">
          registered={readiness.counts.registeredActions} staffGated={readiness.counts.staffGatedMaterializations} manualOnly={readiness.counts.manualOnlyPolicies}
        </p>
        <p className="mt-1">
          missingPolicy={readiness.counts.missingPolicyActions} unsafeMaterializations={readiness.counts.unsafeMaterializations}
        </p>
        <p className="mt-1">
          targetWritesExecuted={String(readiness.targetWritesExecuted)} actionsExecuted={String(readiness.actionsExecuted)} mutatesCrm={String(readiness.mutatesCrm)}
        </p>
        <p className="mt-1">
          serving={readiness.servingDecision} cutover={readiness.cutoverDecision}
        </p>
      </div>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">
          Action smoke checks
        </p>
        {readiness.actionSmoke.checks.map((check) => (
          <div
            key={check.key}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-[var(--color-foreground)]">{check.key}</p>
              <span
                className={`rounded-full px-2 py-1 text-xs font-semibold ${
                  check.ok
                    ? "bg-[var(--color-success)]/10 text-[var(--color-success)]"
                    : "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]"
                }`}
              >
                {check.status}
              </span>
            </div>
            <p className="mt-1 text-[var(--color-muted-foreground)]">{check.summary}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-[var(--color-card)] p-3 text-xs text-[var(--color-muted-foreground)]">
        <p className="font-semibold text-[var(--color-foreground)]">Blocked until explicit operator gates</p>
        <p className="mt-1">
          {readiness.actionSmoke.blockedActions.length > 0 ? readiness.actionSmoke.blockedActions.join(", ") : "No blocked action labels returned."}
        </p>
      </div>
      {readiness.blockers.length > 0 ? (
        <div className="space-y-2">
          {readiness.blockers.map((blocker) => (
            <p
              key={blocker}
              className="rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-card)] p-3 text-sm text-[var(--color-destructive)]"
            >
              {blocker}
            </p>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {readiness.allowedNextActions.map((action) => (
            <p key={action} className="rounded-xl bg-[var(--color-card)] p-3 text-sm text-[var(--color-muted-foreground)]">
              {action}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function signalCount(config: CrmProgramConfigResponse): number {
  return config.scoreComponents.reduce((total, component) => total + component.signals.length, 0);
}

function getMatchedQualificationGateKeys(evidence: CrmProgramPreviewEvidence): string[] {
  return evidence.matchedQualificationGateKeys ?? [];
}

function getMatchedQualificationGateReasonCodes(evidence: CrmProgramPreviewEvidence): string[] {
  return evidence.matchedQualificationGateReasonCodes ?? [];
}

function getMatchedQualificationGateCount(evidence: CrmProgramPreviewEvidence): number {
  return evidence.counts.matchedQualificationGates ?? getMatchedQualificationGateKeys(evidence).length;
}

function formatFactKeyList(factKeys: string[]): string {
  return factKeys.length > 0 ? factKeys.join(", ") : "None";
}

function parseFacts(text: string): { ok: true; value: Record<string, CrmProgramFactValue> } | { ok: false; error: string } {
  try {
    const parsed = JSON.parse(text) as unknown;
    return normalizeCrmProgramPreviewFacts(parsed, "Facts");
  } catch {
    return { ok: false, error: "Facts JSON is invalid." };
  }
}

function getPreviewBoundaryError(preview: CrmProgramPolicyPreviewResponse): string | null {
  return validateCrmProgramPreviewBoundary(preview, "CRM policy preview response")
    ?? validateCrmProgramPreviewEvidenceContract(
      preview.previewEvidence,
      "CRM policy preview evidence",
      preview.configDigestSha256,
    );
}

function getPreviewSuiteBoundaryError(suitePreview: CrmProgramPolicyPreviewSuiteResponse): string | null {
  const rootError = validateCrmProgramPreviewBoundary(suitePreview, "CRM policy preview-suite response")
    ?? validateCrmProgramPreviewBoundary(suitePreview.suite, "CRM policy preview-suite evidence");
  if (rootError) return rootError;

  for (const previewCase of suitePreview.suite.cases) {
    const caseError = validateCrmProgramPreviewEvidenceContract(
      previewCase.previewEvidence,
      `CRM policy preview-suite case '${previewCase.key}' evidence`,
      suitePreview.configDigestSha256,
    );
    if (caseError) return caseError;
  }

  return null;
}

function parsePreviewSuitePayload(text: string): { ok: true; value: CrmProgramPreviewSuitePayload } | { ok: false; error: string } {
  try {
    const parsed = JSON.parse(text) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ok: false, error: "Suite must be a JSON object." };
    }
    const cases = (parsed as { cases?: unknown }).cases;
    if (!Array.isArray(cases) || cases.length === 0 || cases.length > 20) {
      return { ok: false, error: "Suite cases must be an array with 1-20 items." };
    }

    const normalizedCases: CrmProgramPreviewSuitePayload["cases"] = [];
    for (const [index, previewCase] of cases.entries()) {
      if (!previewCase || typeof previewCase !== "object" || Array.isArray(previewCase)) {
        return { ok: false, error: `Suite case ${index + 1} must be an object.` };
      }
      const candidate = previewCase as Record<string, unknown>;
      if (typeof candidate.key !== "string" || candidate.key.trim().length === 0) {
        return { ok: false, error: `Suite case ${index + 1} must include a key.` };
      }
      if (!candidate.facts || typeof candidate.facts !== "object" || Array.isArray(candidate.facts)) {
        return { ok: false, error: `Suite case ${candidate.key} must include facts as an object.` };
      }
      const normalizedFacts = normalizeCrmProgramPreviewFacts(candidate.facts, `Suite case ${candidate.key} facts`);
      if (!normalizedFacts.ok) return normalizedFacts;

      normalizedCases.push({
        key: candidate.key.trim(),
        ...(typeof candidate.label === "string" && candidate.label.trim() ? { label: candidate.label.trim() } : {}),
        facts: normalizedFacts.value,
        ...(parseSuiteExpected(candidate.expected, candidate.key.trim())),
      });
    }

    const candidate = parsed as Record<string, unknown>;
    return {
      ok: true,
      value: {
        ...(typeof candidate.suiteKey === "string" && candidate.suiteKey.trim() ? { suiteKey: candidate.suiteKey.trim() } : {}),
        ...(typeof candidate.suiteVersion === "string" && candidate.suiteVersion.trim() ? { suiteVersion: candidate.suiteVersion.trim() } : {}),
        cases: normalizedCases,
      },
    };
  } catch {
    return { ok: false, error: "Suite JSON is invalid." };
  }
}

function parseSuiteExpected(
  value: unknown,
  caseKey: string,
): { expected?: CrmProgramPreviewSuitePayload["cases"][number]["expected"] } {
  if (value === undefined) return {};
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Suite case ${caseKey} expected must be an object.`);
  }
  const candidate = value as Record<string, unknown>;
  const expected: CrmProgramPreviewSuitePayload["cases"][number]["expected"] = {};
  if (typeof candidate.intentClass === "string" && candidate.intentClass.trim()) expected.intentClass = candidate.intentClass.trim();
  if (typeof candidate.scoreBand === "string" && candidate.scoreBand.trim()) expected.scoreBand = candidate.scoreBand.trim();
  if (candidate.qualificationGate !== undefined) {
    if (!isQualificationGateValue(candidate.qualificationGate)) {
      throw new Error(`Suite case ${caseKey} expected qualificationGate must be unknown, pass, soft_cap, or hard_cap.`);
    }
    expected.qualificationGate = candidate.qualificationGate;
  }
  if (candidate.matchedQualificationGateReasonCodes !== undefined) {
    expected.matchedQualificationGateReasonCodes = parseExpectedReasonCodes(
      candidate.matchedQualificationGateReasonCodes,
      caseKey,
    );
  }
  if (typeof candidate.minScore === "number") expected.minScore = candidate.minScore;
  if (typeof candidate.maxScore === "number") expected.maxScore = candidate.maxScore;
  return Object.keys(expected).length > 0 ? { expected } : {};
}

function isQualificationGateValue(value: unknown): value is CrmProgramPreviewSuiteExpected["qualificationGate"] {
  return value === "unknown" || value === "pass" || value === "soft_cap" || value === "hard_cap";
}

function parseExpectedReasonCodes(value: unknown, caseKey: string): string[] {
  if (!Array.isArray(value) || value.length === 0 || value.length > 20) {
    throw new Error(`Suite case ${caseKey} expected matchedQualificationGateReasonCodes must be an array with 1-20 reason codes.`);
  }

  return Array.from(new Set(value.map((entry, index) => {
    if (typeof entry !== "string" || !entry.trim()) {
      throw new Error(`Suite case ${caseKey} expected matchedQualificationGateReasonCodes[${index}] must be a non-empty string.`);
    }
    const reasonCode = entry.trim();
    if (!/^[A-Za-z][A-Za-z0-9_.:-]*$/.test(reasonCode)) {
      throw new Error(`Suite case ${caseKey} expected matchedQualificationGateReasonCodes[${index}] must be an identifier-like reason code.`);
    }
    return reasonCode;
  }))).sort((left, right) => left.localeCompare(right));
}

function parsePolicyPayload(text: string): { ok: true; value: CrmProgramConfigPayload } | { ok: false; error: string } {
  try {
    const parsed = JSON.parse(text) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { ok: false, error: "Policy JSON must be an object." };
    }
    const { source: _source, ...payload } = parsed as CrmProgramConfigResponse;
    return { ok: true, value: payload };
  } catch {
    return { ok: false, error: "Policy JSON is invalid." };
  }
}

function formatPolicyPayload(config: CrmProgramConfigResponse): string {
  return JSON.stringify(toPolicyPayload(config), null, 2);
}

function formatPreviewSuiteFixtures(fixtures: CrmProgramPreviewSuiteFixtures): string {
  return JSON.stringify({
    suiteKey: fixtures.suiteKey,
    suiteVersion: fixtures.suiteVersion,
    cases: fixtures.cases,
  }, null, 2);
}

function toPolicyPayload(config: CrmProgramConfigResponse): CrmProgramConfigPayload {
  const { source: _source, ...payload } = config;
  return payload;
}

function reviewStatusClass(status: CrmProgramPolicyReviewResponse["status"]): string {
  switch (status) {
    case "ready":
      return "bg-[var(--color-success)]/10 text-[var(--color-success)]";
    case "needs_attention":
      return "bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
    case "blocked":
      return "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
}

function readinessStatusClass(status: CrmProgramPolicyReadinessResponse["status"]): string {
  switch (status) {
    case "ready_for_shadow_dual_run":
      return "bg-[var(--color-success)]/10 text-[var(--color-success)]";
    case "needs_attention":
      return "bg-[var(--color-warning)]/10 text-[var(--color-warning)]";
    case "blocked":
      return "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]";
  }
}

function formatCondition(condition: CrmProgramRuleCondition): string {
  switch (condition.operator) {
    case "default":
      return "default";
    case "all":
    case "any":
      return `${condition.operator}(${condition.conditions.length})`;
    case "fact_present":
      return `${condition.factKey} present`;
    case "fact_absent":
      return `${condition.factKey} absent`;
    case "fact_equals":
      return `${condition.factKey} = ${String(condition.value)}`;
    case "fact_min":
      return `${condition.factKey} >= ${condition.min}`;
    case "date_due":
      return `${condition.factKey} due`;
    case "date_within_days":
      return `${condition.factKey} within ${condition.days}d`;
    case "date_past_within_days":
      return `${condition.factKey} ${condition.minDays}-${condition.maxDays}d ago`;
    case "score_min":
      return `score >= ${condition.min}`;
    case "score_band_is":
      return `band = ${condition.band}`;
    case "score_band_in":
      return `band in ${condition.bands.join(",")}`;
  }
}
